import { Features, QuestionType, type QuestionWithAnswer } from '../../../../generated/hooks';
import { questionSummaryKey } from '@arnold/core';
import type { NewReport } from '../../hooks/useNewReport';

export const SUMMARY_SENTIMENTS = ['negative', 'positive', 'neutral'] as const;
export type SummarySentiment = (typeof SUMMARY_SENTIMENTS)[number];
export interface SummaryPoint {
  /** Source line number remains stable as streamed text grows. */
  id: number;
  sentiment: SummarySentiment;
  text: string;
  questionIndexes: string[];
}

/** Input is taken from the same authorized, anonymity-aware filtered group as the question cards. */
export function buildSummaryAnswers(report: Pick<NewReport, 'rawData' | 'currentGroup'>): QuestionWithAnswer[] {
  const result: QuestionWithAnswer[] = [];
  for (const question of report.rawData?.questions ?? []) {
    if (
      ![QuestionType.Select, QuestionType.Multiselect, QuestionType.Scale, QuestionType.Freetext].includes(
        question.type,
      )
    )
      continue;
    const summary = report.currentGroup?.questionSummaries.get(questionSummaryKey(question.index));
    if (!summary) continue;
    const base = { id: question.index, question: question.text, type: question.type };
    if (question.type === QuestionType.Freetext) {
      const answers = summary.textEntries.map(({ message }) => message.text ?? '').filter((text) => text.length > 4);
      if (answers.length) result.push({ ...base, answers });
    } else {
      // Keep matrix statements separate, but bind them to the parent question card when expanded.
      const statements = question.statementsLocal?.length ? question.statementsLocal : [undefined];
      statements.forEach((statement, index) => {
        const statementSummary =
          statement === undefined
            ? summary
            : report.currentGroup?.questionSummaries.get(questionSummaryKey(question.index, index));
        if (statementSummary?.validCount) {
          result.push({
            ...base,
            question: statement === undefined ? question.text : `${question.text} – ${statement}`,
            choices: statementSummary.choices.map(({ label, count }) => ({ choice: label, count })),
          });
        }
      });
      const answers = summary.commentEntries
        .map(({ message }) => message.comment ?? '')
        .filter((text) => text.length > 4);
      if (answers.length) result.push({ ...base, answers });
    }
  }
  return result;
}

export const hasAiSummaryData = (report: Pick<NewReport, 'rawData' | 'currentGroup'>): boolean =>
  !!report.rawData?.metadata.organization.featureNames?.includes(Features.AiSummaryOfSurveyAndPhase) &&
  buildSummaryAnswers(report).length > 0;

/** Handles legacy sentiment/reference markers and incomplete markers without leaking them into the UI. */
export function parseSummaryPoints(text: string, availableIndexes: ReadonlySet<string>): SummaryPoint[] {
  return text.split('\n').flatMap((line, id) => {
    const marker = line.match(/\[(NEGATIVE|POSITIVE|NEUTRAL)\]/i);
    const bullet = /^\s*(?:[-*•]|\d+[.)])\s+/;
    // Do not render unfinished JSON, bare sentiment tokens, headings or the API's error message as findings.
    if (!marker && !bullet.test(line)) return [];
    const sentiment = (marker?.[1].toLowerCase() ?? 'neutral') as SummarySentiment;
    let content = line
      .replace(bullet, '')
      .replace(/\[(NEGATIVE|POSITIVE|NEUTRAL)\]/gi, '')
      .replace(/^\s*-\s*/, '');
    const questionIndexes: string[] = [];
    content = content.replace(/\[([^\]]*)\]/g, (_, references: string) => {
      references
        .split(',')
        .map((index) => index.trim())
        .forEach((index) => {
          if (availableIndexes.has(index) && !questionIndexes.includes(index)) questionIndexes.push(index);
        });
      return '';
    });
    // API remaps references once a closing bracket arrives; until then the original IDs are not usable.
    content = content.replace(/\[[^\]]*$/, '').trim();
    return content ? [{ id, sentiment, text: content, questionIndexes }] : [];
  });
}
